import {
  Transaction,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import { db } from '../firebaseSetup';
import { allClassNumbers } from '../helpers/academic-years';
import categoriesOrderService from '../services/categories-order-service';
import { CategoriesOrder } from '../types/categories-order';
import { Category, YearSegment } from '../types/category';

class CategoriesService {
  private categoriesCollectionRef = collection(db, 'categories');
  private categoriesOrderCollectionRef = collection(db, 'categoriesorder');
  private batchSize = 10;

  async add(
    category: Category,
    years: Record<number, YearSegment>
  ): Promise<void> {
    const categoryRef = doc(this.categoriesCollectionRef);

    await runTransaction(db, async (transaction) => {
      transaction.set(categoryRef, {
        ...this.omitId(category),
        subjectId: category.subjectId ?? null,
      });

      await this.updateCategoryOrderTransactionsByCategory(
        years,
        category.subjectId!,
        categoryRef.id,
        transaction
      );
    });
  }

  async update(
    category: Category,
    years: Record<number, YearSegment>
  ): Promise<void> {
    if (!category.id) throw new Error('Cannot update a category without an ID');

    const categoryId = category.id.toString();

    await runTransaction(db, async (transaction) => {
      transaction.update(doc(this.categoriesCollectionRef, categoryId), {
        ...this.omitId(category),
        subjectId: category.subjectId ?? null,
      });

      await this.updateCategoryOrderTransactionsByCategory(
        years,
        category.subjectId!,
        categoryId,
        transaction
      );
    });
  }

  async getMany(ids: string[]): Promise<Category[]> {
    const categories: Category[] = [];

    for (let i = 0; i < ids.length; i += this.batchSize) {
      const batchIds = ids.slice(i, i + this.batchSize);
      const q = query(
        this.categoriesCollectionRef,
        where(documentId(), 'in', batchIds)
      );
      const snapshot = await getDocs(q);

      ids.forEach((id) => {
        const doc = snapshot.docs.find((doc) => doc.id === id);
        if (doc) categories.push({ id: doc.id, ...(doc.data() as Category) });
      });
    }

    return categories;
  }

  async getBySubjectIdAndYearAndYearSegment(
    subjectId: string,
    year: number,
    yearSegment: YearSegment
  ): Promise<Category[]> {
    const docRef = doc(
      this.categoriesOrderCollectionRef,
      subjectId === '' ? 'universal' : `${year}${subjectId}`
    );
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error(
        `Categories order entry for subject and year ${subjectId} ${year} does not exist`
      );
    }

    const categoriesOrder = {
      id: docSnap.id,
      ...(docSnap.data() as CategoriesOrder),
    };

    switch (yearSegment) {
      case YearSegment.WholeYear:
        return await this.getMany(categoriesOrder.categoryIdsWholeYear);
      case YearSegment.FirstHalf:
        return await this.getMany([
          ...categoriesOrder.categoryIdsWholeYear,
          ...categoriesOrder.categoryIdsFirstHalf,
        ]);
      case YearSegment.SecondHalf:
        return await this.getMany([
          ...categoriesOrder.categoryIdsWholeYear,
          ...categoriesOrder.categoryIdsSecondHalf,
        ]);
    }
  }

  async getUniversal(): Promise<Category[]> {
    const docRef = doc(this.categoriesOrderCollectionRef, 'universal');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Universal categories order entry does not exist');
    }

    const categoriesOrder = {
      id: docSnap.id,
      ...(docSnap.data() as CategoriesOrder),
    };

    return await this.getMany(categoriesOrder.categoryIdsWholeYear);
  }

  async remove(category: Category): Promise<void> {
    if (!category.id) throw new Error('Cannot delete a category without an ID');

    const categoriesOrders = await this.getCategoriesOrdersByCategory(category);
    const id = category.id.toString();

    for (const categoriesOrder of categoriesOrders) {
      let index = categoriesOrder.categoryIdsWholeYear.indexOf(id);
      if (index !== -1) {
        categoriesOrder.categoryIdsWholeYear.splice(index, 1);
        continue;
      }

      index = categoriesOrder.categoryIdsFirstHalf.indexOf(id);
      if (index !== -1) {
        categoriesOrder.categoryIdsFirstHalf.splice(index, 1);
        continue;
      }

      index = categoriesOrder.categoryIdsSecondHalf.indexOf(id);
      if (index !== -1) categoriesOrder.categoryIdsSecondHalf.splice(index, 1);
    }

    runTransaction(db, async (transaction) => {
      for (const order of categoriesOrders) {
        const { id, ...orderWithoutId } = order;
        transaction.update(
          doc(this.categoriesOrderCollectionRef, id!.toString()),
          orderWithoutId
        );
      }
      transaction.delete(
        doc(this.categoriesCollectionRef, category.id!.toString())
      );
    });
  }

  async doesCategoryExist(id: string): Promise<boolean> {
    const docRef = doc(this.categoriesCollectionRef, id);
    const docSnap = await getDoc(docRef);

    return docSnap.exists();
  }

  private async updateCategoryOrderTransactionsByCategory(
    years: Record<number, YearSegment>,
    subjectId: string,
    categoryId: string,
    transaction: Transaction
  ): Promise<void> {
    const categoriesOrdersToSet: CategoriesOrder[] = [];
    const categoriesOrdersToUpdate: CategoriesOrder[] = [];

    if (!subjectId || subjectId === '') {
      const universalCategoriesOrder =
        await categoriesOrderService.getUniversal();

      if (!universalCategoriesOrder) {
        categoriesOrdersToSet.push({
          id: 'universal',
          categoryIdsWholeYear: [categoryId],
          categoryIdsFirstHalf: [],
          categoryIdsSecondHalf: [],
        });
      } else if (
        !universalCategoriesOrder.categoryIdsWholeYear.includes(categoryId)
      ) {
        universalCategoriesOrder.categoryIdsWholeYear.push(categoryId);
        categoriesOrdersToUpdate.push(universalCategoriesOrder);
      }
    } else {
      await this.updateNonUniversalCategoryOrders(
        years,
        subjectId,
        categoryId,
        categoriesOrdersToSet,
        categoriesOrdersToUpdate
      );
    }

    for (const order of categoriesOrdersToUpdate) {
      const { id, ...orderWithoutId } = order;
      transaction.update(
        doc(this.categoriesOrderCollectionRef, id!.toString()),
        orderWithoutId
      );
    }

    for (const order of categoriesOrdersToSet) {
      const { id, ...orderWithoutId } = order;
      transaction.set(
        doc(this.categoriesOrderCollectionRef, id!.toString()),
        orderWithoutId
      );
    }
  }

  private async updateNonUniversalCategoryOrders(
    years: Record<number, YearSegment>,
    subjectId: string,
    categoryId: string,
    categoriesOrdersToSet: CategoriesOrder[],
    categoriesOrdersToUpdate: CategoriesOrder[]
  ): Promise<void> {
    for (const [year, yearSegment] of Object.entries(years)) {
      let categoriesOrder = await categoriesOrderService.getBySubjectIdAndYear(
        subjectId ?? '',
        Number(year)
      );
      const categoriesOrderFound = categoriesOrder !== null;

      if (!categoriesOrder) {
        categoriesOrder = {
          id: `${year}${subjectId}`,
          categoryIdsWholeYear: [],
          categoryIdsFirstHalf: [],
          categoryIdsSecondHalf: [],
        } as CategoriesOrder;
      }

      switch (yearSegment) {
        case YearSegment.WholeYear:
          this.addIfUnique(categoriesOrder.categoryIdsWholeYear, categoryId);
          this.removeItem(categoriesOrder.categoryIdsFirstHalf, categoryId);
          this.removeItem(categoriesOrder.categoryIdsSecondHalf, categoryId);
          break;
        case YearSegment.FirstHalf:
          this.addIfUnique(categoriesOrder.categoryIdsFirstHalf, categoryId);
          this.removeItem(categoriesOrder.categoryIdsWholeYear, categoryId);
          this.removeItem(categoriesOrder.categoryIdsSecondHalf, categoryId);
          break;
        case YearSegment.SecondHalf:
          this.addIfUnique(categoriesOrder.categoryIdsSecondHalf, categoryId);
          this.removeItem(categoriesOrder.categoryIdsFirstHalf, categoryId);
          this.removeItem(categoriesOrder.categoryIdsWholeYear, categoryId);
          break;
      }

      categoriesOrderFound
        ? categoriesOrdersToUpdate.push(categoriesOrder)
        : categoriesOrdersToSet.push(categoriesOrder);
    }
  }

  private async getCategoriesOrdersByCategory(
    category: Category
  ): Promise<CategoriesOrder[]> {
    if (!category.subjectId) {
      const docRef = doc(this.categoriesOrderCollectionRef, 'universal');
      const docSnap = await getDoc(docRef);
      return docSnap.exists()
        ? [{ id: docSnap.id, ...(docSnap.data() as CategoriesOrder) }]
        : [];
    }

    const categoriesOrders: CategoriesOrder[] = [];

    for (const classNumber of allClassNumbers) {
      const docRef = doc(
        this.categoriesOrderCollectionRef,
        `${classNumber}${category.subjectId}`
      );
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) continue;

      const categoriesOrderData = docSnap.data() as CategoriesOrder;
      categoriesOrders.push({ id: docSnap.id, ...categoriesOrderData });
    }

    return categoriesOrders;
  }

  private addIfUnique(array: string[], value: string) {
    if (!array.includes(value)) {
      array.push(value);
    }
  }

  private removeItem(array: string[], value: string) {
    const index = array.indexOf(value);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  private omitId<T extends { id?: any }>(obj: T): Omit<T, 'id'> {
    const { id: _, ...rest } = obj;
    return rest;
  }
}

const categoriesService = new CategoriesService();
export default categoriesService;
